/**
 * CDD (M32210) mapper — 문서 인스턴스별 값 → [[변수]] payload.
 * 체크박스는 if-wrap 불리언: 선택된 것만 true (나머지는 template 이 ☐).
 *
 * Legal Person 반복부 (TMP004):
 *   [[if shareholders]][[shareholder_name_1]] | [[share_type_1]] | [[share_pct_1]][[/if shareholders]]
 *   [[if bo]][[bo_name_1]] | [[bo_relationship_1]][[/if bo]]
 * → 엔진은 section 이름(shareholders / bo)의 **배열**을 보고 행을 복제하므로
 *   flat `*_1/*_2` 만으로는 매핑되지 않음. 배열 item 키는 템플릿 placeholder(_1)와 동일.
 */
import { CDD_DOCS, CDD_SHAREHOLDER_MAX, type CddDocType } from '../components/cdd/cddFields';

export interface CddDocInput {
  docType: CddDocType;
  values: Record<string, string>;
  checkbox: string; // 선택된 bt_*/at_* (없으면 '')
  shRowCount?: number;
  boRowCount?: number;
}

function maxIndexedKey(values: Record<string, string>, prefix: string, hinted: number): number {
  let max = Math.max(0, Number(hinted) || 0);
  const re = new RegExp(`^${prefix}(\\d+)$`);
  for (const k of Object.keys(values || {})) {
    const m = re.exec(k);
    if (m) max = Math.max(max, Number(m[1]) || 0);
  }
  return Math.min(Math.max(max, 0), CDD_SHAREHOLDER_MAX);
}

/** UI flat 값 → [[if shareholders]] 배열 (템플릿 placeholder 는 *_1 고정). */
export function buildShareholdersLoop(values: Record<string, string>, shRowCount?: number) {
  const max = maxIndexedKey(values, 'shareholder_name_', shRowCount ?? 0)
    || maxIndexedKey(values, 'share_type_', shRowCount ?? 0)
    || maxIndexedKey(values, 'share_pct_', shRowCount ?? 0);
  const rows: Record<string, string>[] = [];
  for (let n = 1; n <= max; n++) {
    const name = String(values[`shareholder_name_${n}`] || '').trim();
    const shareType = String(values[`share_type_${n}`] || '').trim();
    const pct = String(values[`share_pct_${n}`] || '').trim();
    if (!name && !shareType && !pct) continue;
    rows.push({
      // 템플릿: [[shareholder_name_1]] / [[share_type_1]] / [[share_pct_1]]
      shareholder_name_1: name,
      share_type_1: shareType,
      share_pct_1: pct,
      // 템플릿을 *_1 없이 바꿀 경우 대비 alias
      shareholder_name: name,
      share_type: shareType,
      share_pct: pct,
    });
  }
  return rows;
}

/** UI flat 값 → [[if bo]] 배열 (템플릿 placeholder 는 *_1 고정). */
export function buildBoLoop(values: Record<string, string>, boRowCount?: number) {
  const max = maxIndexedKey(values, 'bo_name_', boRowCount ?? 0)
    || maxIndexedKey(values, 'bo_relationship_', boRowCount ?? 0);
  const rows: Record<string, string>[] = [];
  for (let n = 1; n <= max; n++) {
    const name = String(values[`bo_name_${n}`] || '').trim();
    const relationship = String(values[`bo_relationship_${n}`] || '').trim();
    if (!name && !relationship) continue;
    rows.push({
      bo_name_1: name,
      bo_relationship_1: relationship,
      bo_name: name,
      bo_relationship: relationship,
    });
  }
  return rows;
}

export function buildCddVariables(doc: CddDocInput): Record<string, any> {
  const values = doc.values || {};
  const out: Record<string, any> = { ...values };
  if (doc.checkbox) out[doc.checkbox] = true;

  if (doc.docType === 'legal_person') {
    out.shareholders = buildShareholdersLoop(values, doc.shRowCount);
    out.bo = buildBoLoop(values, doc.boRowCount);
  }

  return out;
}

export function cddTemplateId(docType: CddDocType): string {
  return CDD_DOCS[docType].templateId;
}
